import React, { useState, useEffect, useCallback } from 'react'
import {
    Box,
    CircularProgress,
    Container,
    Grid,
    Typography,
    IconButton,
    Divider,
} from '@mui/material'
import {
    AccountBalanceWallet,
    TrendingUp,
    TrendingDown,
    Assessment,
    WarningAmber,
    Refresh,
} from '@mui/icons-material'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import { generateDashboardData } from './mockData'
import PeriodNavigator from './components/PeriodNavigator'
import KPICard from './components/KPICard'
import CashFlowAreaChart from './components/CashFlowAreaChart'
import RevenueExpenseBarChart from './components/RevenueExpenseBarChart'
import RevenueDonutChart from './components/RevenueDonutChart'
import ExpenseDonutChart from './components/ExpenseDonutChart'
import AgingChart from './components/AgingChart'
import WaterfallChart from './components/WaterfallChart'
import SparklineCards from './components/SparklineCards'
import BudgetVsActualChart from './components/BudgetVsActualChart'
import TopClientsChart from './components/TopClientsChart'
import AccountsPayableChart from './components/AccountsPayableChart'

const KPI_ICONS = [
    <AccountBalanceWallet />,
    <TrendingUp />,
    <TrendingDown />,
    <Assessment />,
    <WarningAmber />,
]

const KPI_COLORS = ['#3B82F6', '#22C55E', '#EF4444', '#8B5CF6', '#F59E0B']

export default function CashFlowDashboard() {
    const [period, setPeriod] = useState('quarter')
    const [referenceDate, setReferenceDate] = useState(new Date())
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)

    const refDateParam = `${referenceDate.getFullYear()}-${String(referenceDate.getMonth() + 1).padStart(2, '0')}`

    const fetchData = useCallback(() => {
        setLoading(true)
        const condoId = getCondoId()

        api.get('analytics/financial-dashboard/', {
            params: { condo_id: condoId, period, ref_date: refDateParam },
        })
            .then((response) => setData(response.data))
            .catch((err) => {
                console.error('Dashboard fetch error:', err)
                setData(generateDashboardData(period, referenceDate))
            })
            .finally(() => setLoading(false))
    }, [period, refDateParam, referenceDate])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    return (
        <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
            <Container maxWidth="xl" sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                        Gestão Financeira
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => {
                                setReferenceDate(new Date())
                                fetchData()
                            }}
                            disabled={loading}
                            sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0' }}
                        >
                            <Refresh sx={{ color: '#64748B' }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Period Navigator */}
                <Box sx={{ mb: 3 }}>
                    <PeriodNavigator
                        period={period}
                        onPeriodChange={setPeriod}
                        referenceDate={referenceDate}
                        onReferenceDateChange={setReferenceDate}
                    />
                </Box>

                {/* Loading state */}
                {!data ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* KPI Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {data.kpiData.map((kpi, index) => (
                                <Grid item xs={12} sm={6} md key={kpi.key}>
                                    <KPICard
                                        title={kpi.title}
                                        value={kpi.value}
                                        variation={kpi.variation}
                                        variationLabel={kpi.variationLabel}
                                        icon={KPI_ICONS[index]}
                                        color={KPI_COLORS[index]}
                                        isPercentage={kpi.isPercentage}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Seção Principal */}
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                            Visão Geral
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid item xs={12}>
                                <CashFlowAreaChart data={data.cashFlowData} />
                            </Grid>
                            <Grid item xs={12}>
                                <RevenueExpenseBarChart data={data.revenueExpenseData} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RevenueDonutChart data={data.revenueByCategoryData} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ExpenseDonutChart data={data.expenseByCategoryData} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <AgingChart data={data.agingData} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <WaterfallChart data={data.waterfallData} />
                            </Grid>
                        </Grid>

                        {/* Seção Secundária */}
                        <Divider sx={{ mb: 3 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                            Análises Detalhadas
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <SparklineCards data={data.sparklineData} />
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <BudgetVsActualChart data={data.budgetVsActualData} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TopClientsChart data={data.topClientsData} />
                            </Grid>
                            <Grid item xs={12}>
                                <AccountsPayableChart data={data.accountsPayableData} />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Container>
        </Box>
    )
}
